import type { FeatureFlags } from '@noths/polaris-client-feature-flags';
import { getProductImageURL } from '@noths/polaris-client-utils';

import type { DeliveryState } from 'src/redux/delivery/types';
import type { ProductState } from 'src/redux/product/types';
import type { ProductJSONLDSchema } from 'src/types/productJSONLDSchema';
import { logger } from 'src/utils/serverLogger';
import { getBreadcrumbsLinksForProduct } from '../utils/getBreadcrumbs';
import { isOutOfStock } from '../utils/inventoryStatus';
import { getShippingDetailsJSONLDSchema } from './getShippingDetailsJSONLDSchema';
import { removeAllHtmltags } from './removeAllHtmlTags';

export const getProductJSONLDSchema = (
  product: ProductState,
  delivery: DeliveryState,
  featureFlags?: FeatureFlags,
): ProductJSONLDSchema | null => {
  try {
    const productUrl = product.links?.[0]?.href ? `${product.links[0].href}#product` : '';
    const offerUrl = product.links?.[0]?.href ? `${product.links[0].href}#offer` : '';
    const breadcrumbsLinks = getBreadcrumbsLinksForProduct(product, featureFlags);

    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      '@id': productUrl,
      name: product.title,
      description: removeAllHtmltags(
        product.description +
          product.otherInformation +
          product.materials +
          product.variationsAvailable +
          product.dimensions,
      ),
      image: product.images?.[0]?.href ? getProductImageURL(product.images[0].href, 'preview') : '',
      sku: product.code,
      brand: { '@type': 'Brand', name: product.partner.name },
      offers: {
        '@type': 'Offer',
        '@id': offerUrl,
        url: product.links?.[0]?.href || '',
        priceCurrency: product.price.currency,
        price: String(product.price.amount / 100),
        availability: isOutOfStock(product.inventoryStatus)
          ? 'https://schema.org/OutOfStock'
          : 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        seller: { '@type': 'Organization', name: product.partner.name },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: 'GB',
          merchantReturnDays: 28,
          returnFees: 'https://schema.org/FreeReturn',
          returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        },
        shippingDetails: getShippingDetailsJSONLDSchema(delivery),
      },
      breadcrumbs: {
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbsLinks.map(({ href, title }, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: title,
          item: href,
        })),
      },
      ...(product.numberOfReviews > 0 && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: product.productRating as number,
          reviewCount: product.numberOfReviews,
        },
      }),
    };
  } catch (error) {
    logger.warn('getProductJSONLDSchema error:', error);
    return null;
  }
};
